import { useState, useEffect, useCallback, useRef } from 'react';
import {
    FormHelperText, FormLabel, FormControl, TextField, CircularProgress,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toastr } from 'react-redux-toastr';
import {
    AccordionSetup,
} from '@commonClient2/components';
import { UPDATE_GLOBAL_SETUP } from '../../store/globalSetup';
import * as dataSetManagerService from '../../services/api-endpoints/dataSetManager.service';
import { extractErrorMessage } from '../../../services/api';

import './DatasetFieldLabelsOverride.scss';

/**
 * @param {{
 *  isLoading: boolean;
 *  globalSetups: GlobalSetup.GlobalSetup;
 *  step: number;
 * }} param0
 * */
const DatasetFieldLabelsOverride = ({
    isLoading,
    globalSetups,
    step,
}) => {
    const { t } = useTranslation('globalSetup', { keyPrefix: 'datasetFieldLabelsOverride' });
    const dispatch = useDispatch();
    const { datasetFieldLabelsOverride: rules } = globalSetups;
    const { fieldLabelOverrides } = rules;

    const [availableFields, setAvailableFields] = useState([]);
    const [loadingFields, setLoadingFields] = useState(false);
    const loadingRef = useRef(false);

    /**
     * @template T
     * @param {T} value
     */
    const updateStore = (value) => {
        dispatch({
            type: UPDATE_GLOBAL_SETUP,
            payload: { datasetFieldLabelsOverride: value },
        });
    };

    /**
     * @param {string} fieldKey
     * @param {string} customLabel
     */
    const onChange = (fieldKey, customLabel) => {
        const newOverrides = { ...fieldLabelOverrides };
        if (customLabel && customLabel.trim() !== '') {
            newOverrides[fieldKey] = customLabel;
        } else {
            delete newOverrides[fieldKey];
        }
        updateStore({ fieldLabelOverrides: newOverrides });
    };

    const loadAvailableFields = useCallback(async () => {
        if (loadingRef.current) {
            return;
        }

        loadingRef.current = true;
        setLoadingFields(true);
        try {
            const response = await dataSetManagerService.getAvailableFields();
            const fields = response.data.map((field) => ({
                key: field.key,
                label: field.label || field.key,
            }));
            setAvailableFields(fields);
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            toastr.error(t('error.loadFieldsFailed'), errorMessage);
        } finally {
            setLoadingFields(false);
            loadingRef.current = false;
        }
    }, [t]);

    useEffect(() => {
        loadAvailableFields();
    }, [loadAvailableFields]);

    return (
        <AccordionSetup
            stepIndex={step.toString()}
            headerTitle={t('title')}
            summary=""
            className="dataset-field-labels-override-step"
        >
            <div className="dataset-field-labels-override-container">
                <FormHelperText className="info">{t('description')}</FormHelperText>
                
                {loadingFields ? (
                    <div className="loading-container">
                        <CircularProgress size={24} />
                    </div>
                ) : (
                    <div className="field-overrides-list">
                        {availableFields.map((field) => (
                            <FormControl key={field.key} className="field-override-item">
                                <FormLabel id={`field-label-${field.key}`}>
                                    {field.label}
                                </FormLabel>
                                <TextField
                                    name={`fieldLabelOverride-${field.key}`}
                                    className="field-label-input"
                                    aria-labelledby={`field-label-${field.key}`}
                                    value={fieldLabelOverrides[field.key] || ''}
                                    onChange={({ target: { value } }) => {
                                        onChange(field.key, value);
                                    }}
                                    variant="outlined"
                                    size="small"
                                    placeholder={field.label}
                                    disabled={isLoading}
                                />
                            </FormControl>
                        ))}
                    </div>
                )}
            </div>
        </AccordionSetup>
    );
};

export default DatasetFieldLabelsOverride;

